import { Badge, Box, HStack, Text, VStack } from "@chakra-ui/react";
import {
  DeleteOutline as DeleteOutlineIcon,
  DragIndicator as DragIcon,
  EditOutlined as EditOutlinedIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import type { Identifier } from "dnd-core";
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { IconButton } from "~/src/components/common/IconButton";
import { Switch } from "~/src/components/common/Switch";
import { chakraFactory } from "~/src/lib/chakra-ui";

import { ModuleLinkedGroupsTooltip } from "./ModuleLinkedGroupsTooltip";

const StyledModuleCard = chakraFactory(Box, {
  baseStyle: {
    border: "1px solid #E2E8F0",
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "white",
    _hover: {
      borderColor: "#CBD5E0",
    },
  },
});

const StyledDragHandle = chakraFactory(Box, {
  baseStyle: {
    cursor: "grab",
    color: "#A0AEC0",
    _active: {
      cursor: "grabbing",
    },
  },
});

type ModuleItem = {
  id: string;
  name: string;
  enabled: boolean;
  /** Reflects pending toggle (draftEnabled); use for the list switch UI. */
  effectiveEnabled: boolean;
  priority: number;
  hasDraft: boolean;
  hasPendingToggle?: boolean;
  isArchive?: boolean;
  /** Set when staged for removal or when published as a tombstone (soft remove) */
  deletedAt?: string | null;
  tagContent: string | null;
  draftContent: string | null;
  linkedPopupGroupNames: string[];
  linkedAbtestGroupTitles: string[];
  popupGroupIds: string[];
  abtestScenarioPageGroupIds: string[];
  /** Gating the workspace would publish, so the row sits in the matching list */
  isDistributionGated?: boolean;
  hasPendingLinkChange?: boolean;
};

const DRAG_TYPE = "MODULE_CARD";

type DragItem = {
  index: number;
  id: string;
};

type DraggableModuleCardProps = {
  module: ModuleItem;
  index: number;
  onToggle: (moduleId: string, enabled: boolean) => void;
  onEdit: (module: ModuleItem) => void;
  onLink: (module: ModuleItem) => void;
  onDelete: (moduleId: string) => void;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  onDragEnd?: () => void;
};

const DraggableModuleCard: FC<DraggableModuleCardProps> = ({
  module,
  index,
  onToggle,
  onEdit,
  onLink,
  onDelete,
  moveCard,
  onDragEnd,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: DRAG_TYPE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset?.y ?? 0) - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const isStagedRemoval =
    module.isArchive === true && Boolean(module.deletedAt);
  const isCommittedArchive =
    module.isArchive === false && Boolean(module.deletedAt);
  const isInactiveModule = isStagedRemoval || isCommittedArchive;

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    canDrag: () => !isInactiveModule,
    item: () => {
      return { id: module.id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
      onDragEnd?.();
    },
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  const handleToggleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onToggle(module.id, e.target.checked);
    },
    [module.id, onToggle]
  );

  const handleEditClick = useCallback(() => {
    onEdit(module);
  }, [module, onEdit]);

  const handleLinkClick = useCallback(() => {
    onLink(module);
  }, [module, onLink]);

  const handleDeleteClick = useCallback(() => {
    onDelete(module.id);
  }, [module.id, onDelete]);

  return (
    <StyledModuleCard
      ref={ref}
      w="100%"
      opacity={isInactiveModule ? 0.6 : opacity}
      data-handler-id={handlerId}
    >
      <HStack spacing="12px" align="center">
        <StyledDragHandle
          as={isInactiveModule ? "div" : undefined}
          cursor={isInactiveModule ? "not-allowed" : "grab"}
        >
          <DragIcon fontSize="small" />
        </StyledDragHandle>

        <VStack align="start" spacing="4px" flex="1">
          <HStack spacing="8px" flexWrap="wrap">
            <Text
              fontWeight="medium"
              textDecoration={isInactiveModule ? "line-through" : undefined}
            >
              {module.name}
            </Text>
            {isStagedRemoval && (
              <Badge colorScheme="red" size="sm">
                削除候補
              </Badge>
            )}
            {isCommittedArchive && (
              <Badge colorScheme="gray" size="sm">
                削除済（履歴）
              </Badge>
            )}
            {!isInactiveModule && module.hasDraft && (
              <Badge colorScheme="orange" size="sm">
                下書き
              </Badge>
            )}
            {!isInactiveModule && module.hasPendingToggle && (
              <Badge colorScheme="blue" size="sm">
                切替予定
              </Badge>
            )}
            {!isInactiveModule && module.isDistributionGated && (
              <Badge colorScheme="purple" size="sm">
                配信条件連動
              </Badge>
            )}
            {!isInactiveModule && module.hasPendingLinkChange && (
              <Badge colorScheme="teal" size="sm">
                連動変更予定
              </Badge>
            )}
            {!isInactiveModule && (
              <ModuleLinkedGroupsTooltip
                linkedPopupGroupNames={module.linkedPopupGroupNames}
                linkedAbtestGroupTitles={module.linkedAbtestGroupTitles}
                isDistributionGated={module.isDistributionGated}
              />
            )}
          </HStack>
        </VStack>

        <Switch
          isChecked={module.effectiveEnabled}
          onChange={handleToggleChange}
          isReadOnly={isInactiveModule}
          isDisabled={isInactiveModule}
        />

        <HStack spacing="4px">
          <IconButton
            label="ポップアップ・ABテストをリンク"
            icon={<LinkIcon />}
            size="24px"
            onClick={handleLinkClick}
            isDisabled={isInactiveModule}
          />
          <IconButton
            label="モジュールを編集"
            icon={<EditOutlinedIcon />}
            size="24px"
            onClick={handleEditClick}
            isDisabled={isInactiveModule}
          />
          <IconButton
            label="削除"
            icon={<DeleteOutlineIcon />}
            size="24px"
            onClick={handleDeleteClick}
            isDisabled={isInactiveModule}
          />
        </HStack>
      </HStack>
    </StyledModuleCard>
  );
};

type Props = {
  modules: ModuleItem[];
  onToggle: (moduleId: string, enabled: boolean) => void;
  onEdit: (module: ModuleItem) => void;
  onLink: (module: ModuleItem) => void;
  onDelete: (moduleId: string) => void;
  onReorder: (moduleOrders: { moduleId: string; priority: number }[]) => void;
  emptyTitle?: string;
  emptyDescription?: string;
};

const ModuleListContent: FC<Props> = ({
  modules,
  onToggle,
  onEdit,
  onLink,
  onDelete,
  onReorder,
  emptyTitle = "モジュールがありません",
  emptyDescription = "「モジュール追加」ボタンから新しいモジュールを作成してください",
}) => {
  const [displayModules, setDisplayModules] = useState(() =>
    [...modules].sort((a, b) => a.priority - b.priority)
  );

  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    setDisplayModules((prevModules) => {
      const newModules = [...prevModules];
      const draggedModule = newModules[dragIndex];
      newModules.splice(dragIndex, 1);
      newModules.splice(hoverIndex, 0, draggedModule);
      return newModules;
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    // Update priorities based on new order
    const moduleOrders = displayModules.map((module, index) => ({
      moduleId: module.id,
      priority: index,
    }));
    onReorder(moduleOrders);
  }, [displayModules, onReorder]);

  // Update display modules when props change
  useEffect(() => {
    setDisplayModules([...modules].sort((a, b) => a.priority - b.priority));
  }, [modules]);

  if (modules.length === 0) {
    return (
      <Box textAlign="center" py="24px" color="gray.500">
        <Text>{emptyTitle}</Text>
        <Text fontSize="sm">{emptyDescription}</Text>
      </Box>
    );
  }

  return (
    <VStack spacing="8px" align="stretch">
      {displayModules.map((module, index) => (
        <DraggableModuleCard
          key={module.id}
          module={module}
          index={index}
          onToggle={onToggle}
          onEdit={onEdit}
          onLink={onLink}
          onDelete={onDelete}
          moveCard={moveCard}
          onDragEnd={handleDragEnd}
        />
      ))}
    </VStack>
  );
};

export const ModuleList: FC<Props> = (props) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <ModuleListContent {...props} />
    </DndProvider>
  );
};
